import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Box,
  createFilterOptions,
  Grid2,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import GKModal from "./GKModal";
import { CloseIcon, InfolconIcon } from "./Icons";

interface GKSearchSelectProps extends Omit<
  AutocompleteProps<any, any, any, any, any>,
  "renderInput"
> {
  tooltipText?: string;
  inputLabel?: string;
  requiredField?: boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  helperText?: any;
  error?: any;
  placeholder?: string;
  tooltipModal?: {
    isTrue?: boolean;
    tooltipModelLabel?: string;
    tooltipModalContent?: React.ReactNode;
  };
}

const GKSearchSelect = (props: GKSearchSelectProps) => {
  const {
    inputLabel,
    requiredField,
    disableClearable = true,
    tooltipText,
    helperText,
    error,
    placeholder,
    tooltipModal,
    ...rest
  } = props;

  const [initialSliceValue, setInitialSliceValue] = useState(50);
  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const handleScroll = (event: any) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    if (listboxNode.scrollHeight - position <= 1) {
      setInitialSliceValue((prev) => prev + 100);
    }
  };

  const returnPlaceHolder = () => {
    if (Array.isArray(rest?.value) && rest?.value.length > 0) {
      return "";
    }

    return placeholder || inputLabel || "";
  };

  return (
    <Box display={"flex"} alignItems={"center"} gap={1} width={"100%"}>
      <Autocomplete
        {...rest}
        fullWidth
        disableClearable={disableClearable}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : (option?.version ??
              option?.templateName ??
              option?.companyName ??
              option?.title ??
              option?.name ??
              option?.lable ??
              "")
        }
        filterOptions={createFilterOptions({
          limit: initialSliceValue,
          trim: true,
          ignoreCase: true,
          stringify: (option: any) => {
            return Object.values(option).join(" ");
          },
        })}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
          "& .MuiChip-root": {
            borderRadius: "6px",
            margin: "0 3px",
          },
          "& .MuiFormHelperText-root": {
            marginLeft: 0,
            marginRight: 0,
          },
          mt: inputLabel ? 0.7 : 0,
        }}
        inputValue={searchText}
        onInputChange={(event, val) => {
          setSearchText(val);
        }}
        onKeyDown={(e) => {
          if (
            e.key === "ArrowUp" ||
            e.key === "ArrowDown" ||
            e.key === "ArrowLeft" ||
            e.key === "ArrowRight" ||
            e.key === " "
          ) {
            e.stopPropagation();
          }
        }}
        slotProps={{
          chip: { deleteIcon: <CloseIcon width={16} height={16} /> },
          listbox: { onScroll: handleScroll },
          popper: {
            placement: "bottom",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={inputLabel}
            placeholder={returnPlaceHolder()}
            autoComplete="off"
            slotProps={{
              inputLabel: {
                required: requiredField,
                sx: {
                  "& .MuiFormLabel-asterisk": {
                    color: "red",
                  },
                },
              },
            }}
            error={!!error}
            helperText={helperText}
          />
        )}
      />
      {tooltipText && (
        <Tooltip title={tooltipText} arrow>
          <InfolconIcon />
        </Tooltip>
      )}

      {tooltipModal?.isTrue && (
        <>
          <IconButton size="small" onClick={() => setOpenModal(true)}>
            <InfolconIcon />
          </IconButton>

          <GKModal
            modalTitle={tooltipModal?.tooltipModelLabel}
            open={openModal}
            setOpen={setOpenModal}
            width={500}
          >
            <Grid2 container>{tooltipModal?.tooltipModalContent}</Grid2>
          </GKModal>
        </>
      )}
    </Box>
  );
};

export default GKSearchSelect;
